import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAdminArticles, type ArticleStatus } from "@/hooks/useAdminArticles";
import AdminStatsBar from "@/pages/admin/components/AdminStatsBar";
import AdminFilters from "@/pages/admin/components/AdminFilters";
import AdminArticleRow from "@/pages/admin/components/AdminArticleRow";
import AdminLogsTab from "@/pages/admin/components/AdminLogsTab";

const ADMIN_PASSWORD = "wazi2026";

export default function AdminPage() {
  const [authenticated, setAuthenticated] = useState(() => sessionStorage.getItem("wazi_admin") === "true");
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState(false);

  const [activeTab, setActiveTab] = useState<"articles" | "logs">("articles");
  const [statusFilter, setStatusFilter] = useState<ArticleStatus | "all">("pending");
  const [search, setSearch] = useState("");
  const [scraping, setScraping] = useState(false);
  const [lastScrape, setLastScrape] = useState<string | null>(null);
  const [scrapeResult, setScrapeResult] = useState<{ inserted?: number; skipped?: number; errors?: string[] } | null>(null);
  const [fillingUrls, setFillingUrls] = useState(false);
  const [urlFillResult, setUrlFillResult] = useState<{ updated?: number; errors?: number } | null>(null);

  const { articles, loading, counts, updateStatus, toggleFeatured, triggerScrape, refetch } = useAdminArticles(statusFilter);

  const filtered = useMemo(() => {
    if (!search) return articles;
    const q = search.toLowerCase();
    return articles.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.entrepreneur.toLowerCase().includes(q) ||
        a.source.toLowerCase().includes(q) ||
        a.country.toLowerCase().includes(q)
    );
  }, [articles, search]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      sessionStorage.setItem("wazi_admin", "true");
      setAuthenticated(true);
    } else {
      setPasswordError(true);
      setTimeout(() => setPasswordError(false), 2000);
    }
  };

  const handleScrape = async () => {
    setScraping(true);
    const result = await triggerScrape();
    setScraping(false);
    if (result) {
      setScrapeResult(result);
      setLastScrape(new Date().toLocaleTimeString("fr-FR"));
      setTimeout(() => setScrapeResult(null), 6000);
    }
  };

  const handleFillAllUrls = async () => {
    setFillingUrls(true);
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(`${supabaseUrl}/functions/v1/auto-find-url`, {
        method: "POST",
        headers: { Authorization: `Bearer ${supabaseKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({ fill_all: true }),
      });
      const result = await res.json();
      setUrlFillResult(result);
      setTimeout(() => setUrlFillResult(null), 6000);
      refetch();
    } catch {
      setUrlFillResult({ updated: 0, errors: 1 });
    } finally {
      setFillingUrls(false);
    }
  };

  // ── Login screen ──────────────────────────────────────────
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#fdf8f3] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-orange-500 mx-auto mb-4">
              <i className="ri-shield-keyhole-line text-white text-3xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 font-serif">Panel Admin</h1>
            <p className="text-gray-500 text-sm mt-1">Wazi Stories Afrique</p>
          </div>
          <form onSubmit={handleLogin} className="bg-white rounded-2xl border border-gray-100 p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Mot de passe admin</label>
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="••••••••"
              className={`w-full px-4 py-3 border rounded-xl text-sm focus:outline-none transition-colors mb-4 ${
                passwordError ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-orange-400"
              }`}
              autoFocus
            />
            {passwordError && (
              <p className="text-red-500 text-xs mb-3 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>Mot de passe incorrect
              </p>
            )}
            <button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-xl transition-colors cursor-pointer"
            >
              Accéder au panel
            </button>
          </form>
          <div className="text-center mt-4">
            <Link to="/" className="text-sm text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">
              ← Retour au site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Admin panel ───────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#fdf8f3]">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line text-lg"></i>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-500">
                <i className="ri-shield-keyhole-line text-white text-sm"></i>
              </div>
              <div>
                <h1 className="text-sm font-bold text-gray-900 leading-none">Panel Admin</h1>
                <p className="text-xs text-gray-400">Wazi Stories Afrique</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={refetch}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer"
              title="Rafraîchir"
            >
              <i className="ri-refresh-line text-sm"></i>
            </button>
            <button
              onClick={() => { sessionStorage.removeItem("wazi_admin"); setAuthenticated(false); }}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-line"></i>
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        {/* URL fill result toast */}
        {urlFillResult && (
          <div className="mb-4 bg-orange-50 border border-orange-200 rounded-2xl p-4 flex items-start gap-3">
            <i className="ri-magic-line text-orange-500 text-xl flex-shrink-0 mt-0.5"></i>
            <div>
              <p className="text-orange-800 font-semibold text-sm">URLs complétées !</p>
              <p className="text-orange-600 text-xs mt-0.5">
                {urlFillResult.updated ?? 0} articles mis à jour
                {urlFillResult.errors ? ` · ${urlFillResult.errors} erreur(s)` : ""}
              </p>
            </div>
          </div>
        )}

        {/* Scrape result toast */}
        {scrapeResult && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-2xl p-4 flex items-start gap-3">
            <i className="ri-checkbox-circle-fill text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
            <div>
              <p className="text-green-800 font-semibold text-sm">Scraping terminé !</p>
              <p className="text-green-600 text-xs mt-0.5">
                {scrapeResult.inserted ?? 0} nouveaux articles importés · {scrapeResult.skipped ?? 0} doublons ignorés
                {scrapeResult.errors && scrapeResult.errors.length > 0 && (
                  <span className="text-amber-600"> · {scrapeResult.errors.length} erreur(s)</span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Stats */}
        <AdminStatsBar counts={counts} onScrape={handleScrape} scraping={scraping} lastScrape={lastScrape} onFillUrls={handleFillAllUrls} fillingUrls={fillingUrls} />

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit mb-6">
          <button
            onClick={() => setActiveTab("articles")}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === "articles" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          >
            <i className="ri-article-line mr-2"></i>Articles
            {counts.pending > 0 && <span className="ml-2 bg-amber-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{counts.pending}</span>}
          </button>
          <button
            onClick={() => setActiveTab("logs")}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === "logs" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          >
            <i className="ri-history-line mr-2"></i>Logs scraping
          </button>
        </div>

        {activeTab === "logs" ? (
          <AdminLogsTab />
        ) : (
          <>
            <AdminFilters
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
              search={search}
              onSearchChange={setSearch}
              counts={counts}
            />
            {loading ? (
              <div className="flex flex-col gap-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 h-28 animate-pulse"></div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <i className="ri-inbox-line text-5xl mb-4 block"></i>
                <p className="text-lg font-medium">Aucun article trouvé</p>
                <p className="text-sm mt-1">
                  {statusFilter === "pending"
                    ? "Tous les articles ont été traités — lancez un scraping pour en importer de nouveaux."
                    : "Essayez d'autres filtres."}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <p className="text-sm text-gray-500 mb-2">{filtered.length} article{filtered.length > 1 ? "s" : ""}</p>
                {filtered.map((article) => (
                  <AdminArticleRow
                    key={article.id}
                    article={article}
                    onStatusChange={updateStatus}
                    onToggleFeatured={toggleFeatured}
                    onUrlFound={(id, url) => { console.log(`URL trouvée pour ${id}: ${url}`); }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
